import React, { useEffect, useMemo, useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { useWorkspace } from '../../../context/useWorkspace'
import { Button, StatusPill } from '../../Common'
import {
  listMyOnCallSlots,
  acknowledgeOnCall,
  type MyOnCallSlot,
} from '../../../services/hosPeopleService'
import { workspaceTodayKey } from '../../../services/repeat/keys'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import styles from './OnCallPhoneSection.module.css'

export interface OnCallPhoneSectionProps {
  workspaceId: string
  callerUid?: string
  callerRole?: string | null
  surface?: 'helper' | 'member'
  onError?: (err: string | null) => void
}

function createOpId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `ack_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

export function OnCallPhoneSection({
  workspaceId,
  surface = 'helper',
  onError,
}: OnCallPhoneSectionProps): React.ReactElement {
  const { t } = useNls()

  const { workspaces } = useWorkspace()
  const workspace = workspaces.find((w) => w.id === workspaceId)
  const fromDay = useMemo(
    () => workspaceTodayKey('DAILY', workspace?.timezone),
    [workspace?.timezone],
  )

  const [slots, setSlots] = useState<MyOnCallSlot[]>([])
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [staleMessage, setStaleMessage] = useState<string | null>(null)
  const [ackingKey, setAckingKey] = useState<string | null>(null)
  const [refreshNonce, setRefreshNonce] = useState(0)

  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  )

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)
      return () => {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
      }
    }
  }, [])

  useEffect(() => {
    let ignore = false
    if (!workspaceId) return

    void listMyOnCallSlots({
      workspaceId,
      fromDay,
      days: 7,
    })
      .then((res) => {
        if (ignore) return
        setSlots(res.slots || [])
        setErrorMsg(null)
      })
      .catch((err: unknown) => {
        if (ignore) return
        const msg = getApiErrorMessage(err)
        setErrorMsg(msg)
        onError?.(msg)
      })
      .finally(() => {
        if (ignore) return
        setLoading(false)
      })

    return () => {
      ignore = true
    }
  }, [workspaceId, fromDay, onError, refreshNonce])

  const handleAcknowledge = async (slot: MyOnCallSlot) => {
    if (!isOnline) return
    const slotKey = `${slot.rosterId}_${slot.dayKey}_${slot.kind}`
    setAckingKey(slotKey)
    setErrorMsg(null)
    setStaleMessage(null)

    try {
      const opId = createOpId()
      await acknowledgeOnCall({
        workspaceId,
        rosterId: slot.rosterId,
        version: slot.version,
        dayKey: slot.dayKey,
        kind: slot.kind,
        opId,
      })
      setRefreshNonce((n) => n + 1)
    } catch (err) {
      const msg = getApiErrorMessage(err)
      const isStale =
        /version|roster|unpublished|stale|assignment|precondition/i.test(msg) ||
        (err as { code?: string })?.code === 'failed-precondition' ||
        (err as { code?: string })?.code === 'not-found'
      if (isStale) {
        setStaleMessage(t('NLSHelperOpsOnCallStaleRoster'))
        setRefreshNonce((n) => n + 1)
      } else {
        setErrorMsg(msg)
        onError?.(msg)
      }
    } finally {
      setAckingKey(null)
    }
  }

  const groupedSlots = useMemo(() => {
    const map: Record<string, MyOnCallSlot[]> = {}
    for (const slot of slots) {
      if (!map[slot.dayKey]) {
        map[slot.dayKey] = []
      }
      map[slot.dayKey]?.push(slot)
    }
    return map
  }, [slots])

  const sortedDayKeys = useMemo(() => {
    return Object.keys(groupedSlots).sort()
  }, [groupedSlots])

  const title =
    surface === 'member'
      ? t('NLSMemberOpsOnCallTitle')
      : t('NLSHelperOpsOnCallTitle')
  const desc =
    surface === 'member'
      ? t('NLSMemberOpsOnCallDesc')
      : t('NLSHelperOpsOnCallDesc')

  return (
    <section
      className={styles.section}
      data-testid="ops-oncall-section"
      aria-labelledby="ops-oncall-title"
    >
      <div className={styles.titleGroup}>
        <h2 id="ops-oncall-title" className={styles.sectionTitle}>
          {title}
        </h2>
        <p className={styles.sectionDesc}>{desc}</p>
      </div>

      {staleMessage && (
        <div className={styles.staleNotice} data-testid="oncall-stale-notice">
          {staleMessage}
        </div>
      )}

      {errorMsg && (
        <div className={styles.errorMessage} data-testid="oncall-error">
          {errorMsg}
        </div>
      )}

      {!isOnline && (
        <p className={styles.noticeText} data-testid="oncall-offline-hint">
          {t('NLSHelperOpsOnCallNeedsConnection')}
        </p>
      )}

      {loading && (
        <p className={styles.emptyText}>{t('NLSCommonStatusLoading')}...</p>
      )}

      {!loading && slots.length === 0 && (
        <p className={styles.emptyText} data-testid="oncall-empty">
          {t('NLSHelperOpsOnCallEmpty')}
        </p>
      )}

      {!loading && slots.length > 0 && (
        <div className={styles.slotList}>
          {sortedDayKeys.map((dayKey) => {
            const daySlots = groupedSlots[dayKey] || []
            return (
              <div
                key={dayKey}
                className={styles.dayGroup}
                data-testid={`oncall-day-${dayKey}`}
              >
                <div className={styles.dayHeader}>
                  <span>{dayKey}</span>
                </div>
                {daySlots.map((slot) => {
                  const slotKey = `${slot.rosterId}_${slot.dayKey}_${slot.kind}`
                  const isAcked = slot.ackState === 'ACKED'
                  const isBusy = ackingKey === slotKey

                  return (
                    <div
                      key={slotKey}
                      className={styles.slotCard}
                      data-testid={`oncall-slot-${slot.dayKey}-${slot.kind}`}
                    >
                      <div className={styles.slotInfo}>
                        <div className={styles.placeName}>{slot.placeName}</div>
                        <div className={styles.slotBadges}>
                          <StatusPill tone="info">
                            {slot.kind === 'ON_CALL_PRIMARY'
                              ? t('NLSOpsHosOnCallPrimary')
                              : t('NLSOpsHosOnCallBackup')}
                          </StatusPill>
                          <StatusPill tone={isAcked ? 'success' : 'warning'}>
                            {isAcked
                              ? t('NLSHelperOpsOnCallAcked')
                              : t('NLSHelperOpsOnCallPending')}
                          </StatusPill>
                          {slot.dutyRoleKey && (
                            <span className={styles.dutyRole}>
                              {slot.dutyRoleKey}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={styles.slotActions}>
                        {!isAcked && (
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={!isOnline || isBusy}
                            data-testid={`oncall-ack-${slot.dayKey}-${slot.kind}`}
                            onClick={() => void handleAcknowledge(slot)}
                          >
                            {isBusy ? '...' : t('NLSHelperOpsOnCallAckBtn')}
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
